import { splitEncoding } from '@contentful/live-preview';
import Grid2 from '@mui/material/Unstable_Grid2';

import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import Eyebrow from '@/components/Eyebrow/Eyebrow';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import Link from '@/components/Link/Link';
import type { LinkContent } from '@/contentful';
import useGlobalStyles from '@/styles/global.styles';

import useStyles from './InnerContent.styles';

interface Props {
  eyebrow?: string;
  isOnDark: boolean;
  heading?: string;
  subheading?: string;
  bulletedList?: string[];
  termsAndConditions?: LinkContent;
  termsAndConditionsStatement?: string;
  primaryCta?: LinkContent;
  secondaryCta?: LinkContent;
  horizontalAlignment?: 'Left' | 'Center';
  slideMode?: boolean;
}

export default function InnerContent({
  heading,
  subheading,
  bulletedList,
  termsAndConditions,
  termsAndConditionsStatement,
  primaryCta,
  secondaryCta,
  eyebrow,
  isOnDark,
  horizontalAlignment,
  slideMode,
}: Props) {
  const { classes: globalClasses, cx } = useGlobalStyles({ fixed: false });
  const { classes } = useStyles();
  const isCentered = horizontalAlignment === 'Center';

  const termsAndConditionsText =
    termsAndConditionsStatement && termsAndConditions
      ? `${termsAndConditionsStatement} `
      : termsAndConditionsStatement;

  return (
    <div
      className={cx(classes.flexCol, classes.gapOne, {
        [classes.onDark]: isOnDark,
        [classes.onLight]: !isOnDark,
      })}
    >
      <GridContainer2>
        <Grid2
          xs={slideMode ? 12 : 10}
          md={slideMode ? 12 : 6}
          xsOffset={isCentered ? 1 : 0}
          mdOffset={isCentered ? 3 : 0}
          className={cx(classes.flexCol, classes.gapMinusOne, {
            [classes.centerItems]: isCentered,
          })}
        >
          {eyebrow && <Eyebrow text={eyebrow} isOnDark={isOnDark} />}
          {heading && (
            <h1
              className={cx('primaryTextColor', {
                [classes.heading]: slideMode,
                [globalClasses.h1]: !slideMode,
              })}
            >
              {heading}
            </h1>
          )}
        </Grid2>
      </GridContainer2>
      <GridContainer2>
        <Grid2
          xs={slideMode ? 12 : 10}
          sm={slideMode ? 12 : 6}
          md={slideMode ? 12 : 4}
          xsOffset={isCentered ? 1 : 0}
          smOffset={isCentered ? 3 : 0}
          mdOffset={isCentered ? 4 : 0}
          className={cx(classes.flexCol, classes.gapTwo, {
            [classes.centerItems]: isCentered,
          })}
        >
          {(subheading || bulletedList || termsAndConditions) && (
            <div>
              {subheading && (
                <p
                  className={cx('accentTextColor', {
                    [classes.subheading]: slideMode,
                    [globalClasses.body2]: !slideMode,
                  })}
                >
                  {subheading}
                </p>
              )}
              {bulletedList && (
                <ul
                  className={cx(
                    classes.bulletedList,
                    globalClasses.body,
                    'accentTextColor'
                  )}
                >
                  {bulletedList.map((item, index) => {
                    const { cleaned } = splitEncoding(item);

                    return (
                      <li
                        key={`${cleaned}-${
                          // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                          index
                        }`}
                      >
                        {cleaned}
                      </li>
                    );
                  })}
                </ul>
              )}
              {(termsAndConditions || termsAndConditionsStatement) && (
                <span
                  className={cx(
                    globalClasses.bodyHyperlink,
                    classes.termsAndConditionsText,
                    'accentTextColor'
                  )}
                >
                  {termsAndConditionsText}
                  {termsAndConditions && (
                    <Link
                      className={cx(globalClasses.bodyHyperlink)}
                      href={termsAndConditions.fields.url}
                    >
                      {termsAndConditions.fields.text}
                    </Link>
                  )}
                </span>
              )}
            </div>
          )}

          {primaryCta && (
            <div
              className={cx(classes.flex, classes.gapMinusOne, {
                [classes.centerItems]: isCentered,
              })}
            >
              <Button
                isOnDark={isOnDark}
                variant={ButtonVariant.SECONDARY}
                label={primaryCta.fields.text}
                href={primaryCta.fields.url}
                videoUrl={primaryCta.fields.videoUrl}
              />
              {secondaryCta && (
                <Button
                  isOnDark={isOnDark}
                  variant={ButtonVariant.TERTIARY}
                  label={secondaryCta.fields.text}
                  href={secondaryCta.fields.url}
                />
              )}
            </div>
          )}
        </Grid2>
      </GridContainer2>
    </div>
  );
}
